import { Col, Form, FormInstance, Input, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppDispatch,
  dynamicSet,
  getChildOrganizationList,
  getChildOrganizationListClear,
  getChildOrganizationListRequest,
  getLevel,
  getLevelClear,
  getLevelRequest,
  getMyOrganization,
  getMyOrganizationRequest,
  getOrganization,
  getOrganizationRequest,
  getParentHierarchy,
  Organization,
  useDynamicSelector,
} from "@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { isArray } from "lodash";
import store from "@src/redux/store";
import OrganizationSelect from "../../kits/kits-transfer-form/OrganizationSelect";
import DynamicSelect from "../../kits/kits-transfer-form/DynamicSelect";
import DynamicOrgSelect from "./DynamicOrgSelect";

interface DynamicTreeSelectProps {
  form: FormInstance;
  col?: number;
  mutationType?: string;
  kitAssignCheck?: boolean;
  isRequired: boolean;
  allowClear: boolean;
  className?: string;
  showLevelAttribute?: boolean;
  organizationId?: string;
  key?: string;
  showAllLabel?: boolean;
  setLastOrgId?: (id: string) => void;
}

const DynamicOrgFilter: React.FC<DynamicTreeSelectProps> = (props) => {
  const {
    form,
    col,
    mutationType,
    kitAssignCheck = false,
    isRequired,
    allowClear,
    className,
    showLevelAttribute = false,
    organizationId,
    key = "form",
    showAllLabel = true,
    setLastOrgId,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [childListItems, setChildListItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [labels, setLabels] = useState<any>([]); // Store dynamic labels
  const organizationSelectIds = Form.useWatch("organizationId", form);
  const levelAttributeId = Form.useWatch("levelAttributeId", form);
  const { levelName } = useDynamicSelector("level_name");
  const [childOrganization, setChildOrganization] = useState<
    Organization[] | null
  >([]);
  const [levelDetails, setLevelDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState<Boolean>(false);
  const [lastSelectionIndex, setLastSelectionIndex] = useState<number>(-1);
  const [lastSelectedParentId, setLastSelectedParentId] = useState<
    string | null
  >(null);
  const [myOrganizationResult, setMyOrganizationResult] =
    useState<Organization | null>();

  const { result: getSubjectOrganization } =
    useSelector(getParentHierarchy);

  const handleGetMyOrganization = async () => {
    setLoading(true);
    await dispatch(getMyOrganizationRequest({})).unwrap();
    const state = store.getState();
    let getOrg = await getMyOrganization(state);
    setLoading(false);
    setMyOrganizationResult(getOrg?.result);
    // Initialize labels as the full chain (e.g., City → Zone → Division → UPHC → PHC)
    try {
      const list: any[] =
        getOrg?.result?.rootOrganization?.rootLevelList || [];
      // Leaf-level users have no sub-levels to drill into; skip the filter so
      // it doesn't fetch nonexistent child orgs and render an empty "No data"
      // dropdown (see TreeSelect.tsx for the full rationale). Only act on a
      // fully-resolved level object to avoid clobbering non-leaf users during
      // the brief pending/null window of a concurrent getMyOrganization call.
      const userLevel = getOrg?.result?.level;
      const isLeafUser =
        !!userLevel && (userLevel.childLevelList?.length || 0) === 0;
      if (isLeafUser) {
        setLabels([]);
        setChildListItems([]);
      } else if (Array.isArray(list) && list.length > 0) {
        const byId: Record<string, any> = Object.fromEntries(
          list.map((l: any) => [l.id, l])
        );
        const root: any | undefined = list.find((l: any) => !l.parentLevelId);
        const chainNames: string[] = [];
        let current: any | undefined = root;
        // Walk the chain using parentLevelId relationships to ensure order
        while (current) {
          chainNames.push(current.name as string);
          const next: any | undefined = list.find(
            (l: any) => l.parentLevelId === (current as any).id
          );
          current = next;
        }
        // Drop the first level (e.g., Project), keep City..PHC
        const ordered = chainNames.slice(1);
        // Start labels from the current user's level (e.g., Division), not always City
        const currentLevelName: string | undefined =
          getOrg?.result?.level?.name;
        const startIdx = currentLevelName
          ? ordered.indexOf(currentLevelName)
          : -1;
        const effective = startIdx >= 0 ? ordered.slice(startIdx) : ordered;
        setLabels(effective);
        // If user's current level equals first label and top OrganizationSelect is shown,
        // do NOT create a duplicate dynamic select for it. Start from the next level.
        const firstLabel: string | undefined =
          effective.length > 0 ? effective[0] : undefined;
        const showTopOrg = !className;
        const isUserAtFirstLabel = !!(
          firstLabel && getOrg?.result?.level?.name === firstLabel
        );
        const shouldSkipFirstDynamic = showTopOrg && isUserAtFirstLabel;
        setChildListItems(
          shouldSkipFirstDynamic ? [] : firstLabel ? [{ [firstLabel]: [] }] : []
        );
      }
    } catch (e) {
      // noop
    }
    return getOrg.result;
  };
  const handleGetOrganization = async () => {
    setLoading(true);
    await dispatch(
      getOrganizationRequest({ filter: { id: organizationId } })
    ).unwrap();

    const state = store.getState();
    let getOrg = await getOrganization(state);
    setLoading(false);

    return getOrg.result;
  };

  const fetchChildLevelList = async (
    _id?: any,
    levelAttributeIdList?: { id: string; value: string }[]
  ) => {
    setLoading(true);
    await dispatch(
      getChildOrganizationListRequest({
        filter: {
          parentOrganizationId: _id,
          levelAttributeValueList: levelAttributeIdList || undefined,
        },
        pagination: {
          pageLimit: 1000,
          pageNumber: 1,
        },
        sort: [
          {
            field: "name",
            order: "asc",
          },
        ],
      })
    ).unwrap();

    const state = store.getState();
    let childOrganizationList = await getChildOrganizationList(state);
    setLoading(false);
    setChildOrganization(
      childOrganizationList?.result?.items &&
        childOrganizationList?.result?.items?.length > 0
        ? childOrganizationList?.result?.items
        : []
    );

    // Also derive current level from API response to compute next level label immediately
    const apiLevel = childOrganizationList?.result?.items?.[0]?.level || null;
    if (apiLevel) {
      setLevelDetails(apiLevel);
    }
  };

  // Helper: Enforce next level label order from my organization root levels
  const getNextLevelNameByLevelId = (levelId?: string) => {
    if (!levelId) return undefined;
    const list =
      myOrganizationResult?.rootOrganization?.rootLevelList || [];
    const found = list.find((lvl: any) => lvl?.id === levelId);
    return found?.childLevelList?.[0]?.name;
  };

  const fetchLevelData = async (_id: any, name: any = undefined) => {
    setLoading(true);
    await dispatch(
      getLevelRequest({
        filter: {
          id: _id,
          name: name,
        },
      })
    );
    const state = store.getState();
    let levelDetails = await getLevel(state);
    setLoading(false);
    setLevelDetails(levelDetails?.result);
  };

  const handleChange = (
    _id: string,
    label: string,
    organization: any,
    index: number
  ) => {
    setLastSelectionIndex(index);
    setLastSelectedParentId(_id);
    if (setLastOrgId) {
      setLastOrgId(_id);
    }
    form.setFieldsValue({
      organizationId: { [index + 1]: null },
    });
    form.setFieldsValue({
      levelAttributeId: [],
    });
    setSelectedIds((prev) => {
      const updatedSelectedIds = [...prev];
      updatedSelectedIds[index] = _id;

      return updatedSelectedIds.slice(0, index + 1);
    });

    setChildListItems((prev) => {
      const updatedChildListItems = [...prev];
      updatedChildListItems[index] = {
        [label]: updatedChildListItems[index]?.[label],
      };
      return updatedChildListItems.slice(0, index + 1);
    });

    // City select should load children (Zones) based on selected City's id
    // We only need to fetch children by parent id; no immediate level fetch is required.
    fetchChildLevelList(_id);
  };

  const handleChangeLevelAttributes = (
    _id: string,
    label: string,
    organization: any,
    index: number
  ) => {
    setLastSelectionIndex(index);
    setLastSelectedParentId(_id);
    dispatch(dynamicSet("level_name", { levelName: label }));
    form.setFieldsValue({
      organizationId: { [index + 1]: null },
    });

    let organizationId =
      organizationSelectIds?.[organizationSelectIds?.length - 1] ||
      myOrganizationResult?.id;

    setSelectedIds((prev) => {
      const updatedSelectedIds = [...prev];
      updatedSelectedIds[index] = _id;

      return updatedSelectedIds.slice(0, index + 1);
    });

    setChildListItems((prev) => {
      const updatedChildListItems = [...prev];
      updatedChildListItems[index] = {
        [label]: updatedChildListItems[index]?.[label] || null,
      };
      return updatedChildListItems.slice(0, index + 1);
    });

    if (organizationId) {
      fetchChildLevelList(
        organizationId,
        levelAttributeId?.filter((list) => list?.value)
      );
      // fetchLevelData(undefined, label);
    }
  };

  const findLevelAttributeList = (data: any[], targetId: string) => {
    for (const item of data) {
      for (const key in item) {
        const levels = item[key];
        for (const levelObj of levels) {
          if (levelObj.id === targetId) {
            const levelAttributes = levelObj?.level?.levelAttributeList;
            return { [levelObj?.level?.name]: levelAttributes };
          }
        }
      }
    }
    return {};
  };

  useEffect(() => {
    if (mutationType === "edit") setChildListItems([]);
  }, []);
  useEffect(() => {
    if (childOrganization !== null && selectedIds?.length > 0) {
      // If user's current level equals first label and the top OrganizationSelect is shown,
      // treat the first label as already selected and start populating from the NEXT label.
      const viewingHasTopOrg = !className;
      const userCurrentLevelName: string | undefined =
        myOrganizationResult?.level?.name;
      let currentIndex = lastSelectionIndex;
      const skipFirstDynamic =
        viewingHasTopOrg &&
        !!labels?.[0] &&
        !!userCurrentLevelName &&
        labels[0] === userCurrentLevelName;
      if (currentIndex < 0 && skipFirstDynamic) {
        // Keep as -1 so the first dynamic slot is index 0
        currentIndex = -1;
      }
      const nextIndex = currentIndex + 1;
      // Compute label index consistently so chain reaches the last level dynamically
      const labelIndex = nextIndex + (skipFirstDynamic ? 1 : 0);
      const nextLevelName = labels?.[labelIndex] || "";
      // Always use immediate children from items for the next level options
      const newOptions = (childOrganization || []).map((c: any) => ({
        id: c.id,
        // Prefer shortName if available for all levels
        name: c.shortName || c.name,
        level: c.level,
        parentOrganizationId: c.parentOrganizationId,
      }));

      if (kitAssignCheck && levelDetails?.allowKitAssign === true) {
        setLevelDetails(null);
        setChildListItems((prev) => {
          const clone = [...prev];
          const upto = clone.slice(0, nextIndex); // keep previous selects only
          if (nextLevelName) upto[nextIndex] = { [nextLevelName]: newOptions };
          return upto;
        });
        if (!labels.includes(nextLevelName)) {
          setLabels([...labels, nextLevelName]);
        }
        setChildOrganization(null);
      } else if (!kitAssignCheck) {
        setChildListItems((prev) => {
          const clone = [...prev];
          const upto = clone.slice(0, nextIndex);
          if (nextLevelName) {
            // Ensure we fill sequentially from index 0
            upto[nextIndex] = { [nextLevelName]: newOptions };
          }
          return upto;
        });
        if (!labels.includes(nextLevelName)) {
          setLabels([...labels, nextLevelName]);
        }
        setChildOrganization(null);
      }
      dispatch(getLevelClear());
      dispatch(getChildOrganizationListClear());
    } else if (
      childOrganization &&
      childOrganization?.length > 0 &&
      levelName
    ) {
      const isFirstLevel = levelName === labels?.[0];
      const newOptions = (childOrganization || []).map((c: any) => ({
        id: c.id,
        name: c.shortName || c.name,
        level: c.level,
        parentOrganizationId: c.parentOrganizationId,
      }));
      setChildListItems((prevChildListItems) =>
        prevChildListItems.map((item) =>
          item.hasOwnProperty(levelName) ? { [levelName]: newOptions } : item
        )
      );
      setChildOrganization(null);
      dispatch(getChildOrganizationListClear());
    }
  }, [
    childOrganization,
    levelDetails,
    kitAssignCheck,
    selectedIds,
    levelName,
    labels,
    lastSelectionIndex,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = organizationId
          ? await handleGetOrganization()
          : await handleGetMyOrganization();
        // Leaf-level users have nothing to drill into — skip fetching child
        // orgs that would only populate an empty "No data" dropdown.
        const canDrillDown =
          !!organizationId ||
          (result?.level?.childLevelList?.length || 0) > 0;
        if (
          (organizationId || result?.id) &&
          mutationType === "add" &&
          canDrillDown
        ) {
          const initialParentId = result?.id;
          setSelectedIds((prevSelectedIds) => [
            ...prevSelectedIds,
            initialParentId,
          ]);
          await fetchChildLevelList(initialParentId);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
      }
    };

    fetchData();
  }, [mutationType]);

  useEffect(() => {
    if (
      getSubjectOrganization?.items &&
      getSubjectOrganization?.items?.length > 0 &&
      mutationType === "edit"
    ) {
      let reAssign = getSubjectOrganization?.items
        .map((list: any) => {
          let getOrganizationList = list.organizationList.map(
            (organization: any) => {
              let findOne =
                myOrganizationResult?.rootOrganization?.rootLevelList?.find(
                  (findLevel: any) => findLevel?.name === list?.level?.name
                );
              return {
                ...organization,
                level: {
                  id: findOne?.id,
                  name: findOne?.name,
                  childLevelList: findOne?.childLevelList,
                },
              };
            }
          );

          return {
            [list?.level?.name]: getOrganizationList,
          };
        })
        .slice(1);
      let formSetValues = getSubjectOrganization?.items
        .map((list) => list.organizationId)
        .slice(1);

      let getLabels = getSubjectOrganization?.items
        ?.map((list) => list?.level?.name)
        ?.slice(1);
      setLabels(getLabels);
      setChildListItems(reAssign);
      form.setFieldsValue({ organizationId: formSetValues });
    }
  }, [getSubjectOrganization, mutationType]);

  useEffect(() => {
    // Labels are now set when pushing child options; avoid duplicate appends here
  }, [levelDetails, kitAssignCheck]);

  useEffect(() => {
    let organizationId = form.getFieldValue("organizationId");
    let getId =
      (organizationId?.length > 0 &&
        organizationId[organizationId.length - 1]) ||
      (organizationId?.length > 1 &&
        organizationId[organizationId.length - 2]);
    form.setFieldValue("my_org", getId || myOrganizationResult?.id);
  }, [form, myOrganizationResult, organizationSelectIds]);

  return (
    <>
      {!className && (
        <Col span={col || 12}>
          <OrganizationSelect
            form={form}
            className={className}
            myOrganizationResult={myOrganizationResult}
          />
        </Col>
      )}
      {childListItems?.length > 0 &&
        childListItems?.map((item, index) => {
          let getAttributesList: any = [];
          const showTopOrg = !className;
          const userLevelName: string | undefined =
            myOrganizationResult?.level?.name;
          const skipFirstDynamic =
            showTopOrg &&
            !!labels?.[0] &&
            !!userLevelName &&
            labels[0] === userLevelName;
          const keyLabel = labels?.[index + (skipFirstDynamic ? 1 : 0)];

          // Check if organization_select_ids is an array and has at least two elements
          if (
            Array.isArray(organizationSelectIds) &&
            (organizationSelectIds.length > 0 ||
              organizationSelectIds.length > 1)
          ) {
            // Use the last or second-to-last element, whichever is available
            const selectedId =
              organizationSelectIds[organizationSelectIds.length - 1] ||
              organizationSelectIds[organizationSelectIds.length - 2];

            // Ensure selectedId is valid before using it
            if (selectedId) {
              getAttributesList = findLevelAttributeList(
                childListItems,
                selectedId
              );
            }
          }

          return (
            <Col span={col || 12} key={index}>
              <DynamicOrgSelect
                form={form}
                options={(item && keyLabel && item[keyLabel]) || []}
                handleChange={handleChange}
                handleChangeLevelAttributes={handleChangeLevelAttributes}
                index={index}
                label={keyLabel || t("Select childLevelList")}
                isRequired={isRequired}
                allowClear={allowClear}
                className={className}
                levelAttributes={getAttributesList}
                showLevelAttribute={false}
                myOrganizationResult={myOrganizationResult}
                showAllLabel={showAllLabel}
              />
            </Col>
          );
        })}
      {loading && (
        <Col span={col || 12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              marginTop:"25px"
            }}
          >
            <Spin />
          </div>
        </Col>
      )}
      <Form.Item
        name={"my_org"}
        initialValue={myOrganizationResult?.id}
        hidden
      >
        <Input />
      </Form.Item>
    </>
  );
};

export default DynamicOrgFilter;
